import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import {
  FiEdit2,
  FiPlus,
  FiTrash2,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiFileText,
  FiFilter,
  FiSearch,
  FiEye,
  FiLock,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Api from '../../Api';
import { usePermission } from '../../hooks/usePermission';
import {
  Button,
  Card,
  Modal,
  PageComponent,
  Skeletons,
  Table,
  TableActions,
  AdjustmentForm,
  AdjustmentAnalysisForm,
  Input,
  Select,
  MultiSelect,
  DatePeriodFilter,
  getDateParams
} from '../../components';
import '../CompanyManager/style.css';

export default function AdjustmentManager() {
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAdjustment, setSelectedAdjustment] = useState(null);
  const [analyzingAdjustment, setAnalyzingAdjustment] = useState(null);
  const [company, setCompany] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('all');
  const [companyFilterValues, setCompanyFilterValues] = useState(['all']);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [adjustmentToDelete, setAdjustmentToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // Filter States
  const [currentDate, setCurrentDate] = useState(new Date());
  const [periodMode, setPeriodMode] = useState('monthly');
  const [customPeriod, setCustomPeriod] = useState({ startDate: '', endDate: '' });
  
  const [selectedServiceStation, setSelectedServiceStation] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  
  const [serviceStations, setServiceStations] = useState([]);
  const [employees, setEmployees] = useState([]);

  const navigate = useNavigate();
  const permissions = usePermission('time_keeping');

  useEffect(() => {
    (async () => {
      try {
        const data = await Api.getCompanies();
        const list = Array.isArray(data) ? data : (data.data || []);
        setCompanies(list);
        setSelectedCompanyId('all');
      } catch (error) {
        toast.error('Erro ao carregar empresas.');
      }
    })();
  }, []);

  useEffect(() => {
    console.log("====")
    console.log(permissions)
    if (!selectedCompanyId || companies.length === 0) return;
    if (!permissions.canView) return;
    const selected = companies.find(c => String(c.id) === String(selectedCompanyId));
    setCompany(selected || null);
    loadAdjustments(selectedCompanyId);
  }, [selectedCompanyId, companies, permissions.canView]);

  useEffect(() => {
    if (!selectedCompanyId) return;
    if (selectedCompanyId === 'all') {
      const ids = companies.map(c => c.id);
      Promise.all(ids.map(id => Api.getServiceStations(id)))
        .then(res => {
          const merged = res.flat();
          if (Array.isArray(merged)) {
            setServiceStations(merged.map(s => ({ value: String(s.id), label: s.name })));
          }
        })
        .catch(console.error);
      Promise.all(ids.map(id => Api.getEmployees(id)))
        .then(res => {
          const merged = res.flat();
          if (Array.isArray(merged)) {
            setEmployees(merged.map(e => ({ value: String(e.id), label: e.name })));
          }
        })
        .catch(console.error);
    } else {
      Api.getServiceStations(selectedCompanyId)
        .then(data => {
          if (Array.isArray(data)) {
            setServiceStations(data.map(s => ({ value: String(s.id), label: s.name })));
          }
        })
        .catch(console.error);
      Api.getEmployees(selectedCompanyId)
        .then(data => {
          if (Array.isArray(data)) {
            setEmployees(data.map(e => ({ value: String(e.id), label: e.name })));
          }
        })
        .catch(console.error);
    }
  }, [selectedCompanyId, companies]);


  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  async function loadAdjustments(companyId) {
    try {
      setLoading(true);
      let raw = [];
      if (companyId === 'all') {
        const ids = companies.map(c => c.id);
        const responses = await Promise.all(ids.map(id => Api.getAdjustments(id)));
        raw = responses.flatMap(d => (Array.isArray(d) ? d : (d && d.data ? d.data : [])));
      } else {
        const data = await Api.getAdjustments(companyId);
        raw = Array.isArray(data) ? data : (data && data.data ? data.data : []);
      }

      if (Array.isArray(raw)) {
        const uniqueMap = raw.reduce((acc, curr) => {
          let dateStr = curr.date;
          if (dateStr && typeof dateStr === 'string' && dateStr.includes('T')) {
            dateStr = dateStr.split('T')[0];
          } else if (dateStr instanceof Date) {
            dateStr = dateStr.toISOString().split('T')[0];
          }
          const key = `${curr.employee_id}_${dateStr}`;
          if (!acc[key] || new Date(curr.createdAt).getTime() > new Date(acc[key].createdAt).getTime()) {
            acc[key] = curr;
          }
          return acc;
        }, {});
        const uniqueAdjustments = Object.values(uniqueMap);
        uniqueAdjustments.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        setAdjustments(uniqueAdjustments);
      } else {
        setError('Não foi possível carregar as solicitações.');
      }
    } catch (err) {
      setError('Erro ao carregar solicitações. Tente novamente.');
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  }

  function handleCreate() {
    setSelectedAdjustment(null);
    setAnalyzingAdjustment(null);
    setIsModalOpen(true);
  }

  function handleAnalyze(adjustment) {
    setAnalyzingAdjustment(adjustment);
    setSelectedAdjustment(null);
    setIsModalOpen(true);
  }

  function handleEdit(adjustment) {
    setSelectedAdjustment(adjustment);
    setAnalyzingAdjustment(null);
    setIsModalOpen(true);
  }

  function handleDelete(adjustment) {
    setAdjustmentToDelete(adjustment);
    setIsDeleteModalOpen(true);
  }

  function handleCancelDelete() {
    if (isDeleting) return;
    setIsDeleteModalOpen(false);
    setAdjustmentToDelete(null);
  }

  async function handleConfirmDelete() {
    if (!adjustmentToDelete) return;

    try {
      setIsDeleting(true);
      await Api.deleteAdjustment(adjustmentToDelete.company_id || selectedCompanyId, adjustmentToDelete.id);
      loadAdjustments(selectedCompanyId);
      setIsDeleteModalOpen(false);
      setAdjustmentToDelete(null);
      toast.success('Solicitação excluída com sucesso!');
    } catch (error) {
      toast.error('Erro ao excluir solicitação.');
    } finally {
      setIsDeleting(false);
    }
  }

  function handleFormSuccess() {
    setIsModalOpen(false);
    loadAdjustments(selectedCompanyId);
  }

  const filteredAdjustments = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const { startDate, endDate } = getDateParams(periodMode, periodMode === 'custom' ? customPeriod : currentDate);
    const allValue = 'all';
    const specificIds = companyFilterValues.filter(v => v !== allValue);
    const hasAllSelected = companyFilterValues.includes(allValue);

    return adjustments.filter(item => {
      // Date Filter
      if (startDate && endDate) {
        const itemDate = item.date; // already YYYY-MM-DD
        if (itemDate < startDate || itemDate > endDate) {
          return false;
        }
      }

      // Search Term
      const matchesSearch =
        !term ||
        (item.employee?.name || '').toLowerCase().includes(term) ||
        (item.reason || '').toLowerCase().includes(term);

      // Status Filter
      let matchesStatus = true;
      if (statusFilter && statusFilter !== 'all') {
        matchesStatus = item.status === statusFilter;
      }

      // Service Station Filter
      let matchesStation = true;
      if (selectedServiceStation) {
        matchesStation = item.service_station_id === Number(selectedServiceStation);
      }

      // Employee Filter
      let matchesEmployee = true;
      if (selectedEmployee) {
        matchesEmployee = item.employee_id === Number(selectedEmployee);
      }

      // Company Filter
      const companyId = item.company_id;
      const matchesCompany =
        hasAllSelected ||
        specificIds.length === 0 ||
        specificIds.includes(String(companyId));

      return matchesSearch && matchesStatus && matchesStation && matchesEmployee && matchesCompany;
    });
  }, [adjustments, searchTerm, statusFilter, periodMode, currentDate, selectedServiceStation, selectedEmployee, companyFilterValues]);

  const totalItems = filteredAdjustments.length;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1;
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const paginatedAdjustments = filteredAdjustments.slice(
    startIndex,
    startIndex + pageSize,
  );

  const columns = [
    {
      id: 'date',
      header: 'Data da Solicitação',
      width: '120px',
      render: (item) => new Date(item.createdAt).toLocaleDateString('pt-BR', { timeZone: 'UTC' }),
    },
    {
      id: 'competenceDate',
      header: 'Data de Competência',
      width: '150px',
      render: (item) => item.competence_date ? new Date(item.competence_date).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '-',
    },
    {
      id: 'company',
      header: 'Empresa',
      width: '1fr',
      render: (item) => {
        const companyObj = companies.find(c => String(c.id) === String(item.company_id));
        return companyObj
          ? (companyObj.corporate_name || companyObj.trade_name || companyObj.name || `Empresa ${companyObj.id}`)
          : (item.company?.name || (item.company_id ? `Empresa ${item.company_id}` : '-'));
      },
    },
    {
      id: 'serviceStation',
      header: 'Posto de Serviço',
      render: (item) => item.serviceStation?.name || '-',
    },
    {
      id: 'employee',
      header: 'Funcionário',
      render: (item) => (
        <div>
          <div>{item.employee?.name || '-'}</div>
          <div style={{ fontSize: '0.8rem', color: '#666' }}>
            {item.employee?.position?.name || '-'} | {item.employee?.journey?.name || '-'}
          </div>
        </div>
      ),
    },
    {
      id: 'reason',
      header: 'Motivo do Funcionário',
      render: (item) => item.reason,
    },
    {
      id: 'status',
      header: 'Status',
      width: '250px',
      render: (item) => {
        const statusMap = {
          pending: { label: 'Pendente', color: '#ff9800', icon: FiClock },
          approved: { label: 'Aprovado', color: '#4caf50', icon: FiCheckCircle },
          rejected: { label: 'Rejeitado', color: '#f44336', icon: FiXCircle },
        };
        const status = statusMap[item.status] || statusMap.pending;
        const Icon = status.icon;

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: status.color }}>
            <Icon size={16} />
            <span>{status.label}</span>
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: 'Ações',
      width: '0.6fr',
      render: (item) => (
        <TableActions>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleAnalyze(item);
            }}
            title="Analisar/Visualizar"
          >
            <FiEye size={16} /> 
            Analisar
          </button>
          <button
            type="button"
            className="action-danger"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(item);
            }}
            title="Excluir"
          >
            <FiTrash2 size={16} />
            Excluir
          </button>
        </TableActions>
      ),
    },
  ];

  function AdjustmentManagerSkeletonWrapper() {
    const { CompanyManagerSkeleton } = Skeletons;
    return <CompanyManagerSkeleton />;
  }

  if (!permissions.canView) {
    return (
      <PageComponent authenticatedPage={true}>
        <div className="container">
          <div className="manager-header">
            <div className="manager-header-title">
              <div className="manager-header-icon">
                <FiLock size={22} />
              </div>
              <div>
                <h1>Acesso Negado</h1>
                <p>Você não tem permissão para visualizar esta página.</p>
              </div>
            </div>
          </div>
        </div>
      </PageComponent>
    );
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="container">
        {loading && !hasLoadedOnce && <AdjustmentManagerSkeletonWrapper />}

        {(hasLoadedOnce || !loading) && (
          <>
            <div className="manager-header">
              <div className="manager-header-title">
                <div className="manager-header-icon">
                  <FiClock size={22} />
                </div>
                <div>
                  <h1>Solicitação de Ajustes</h1>
                  <p>
                    Gerencie as solicitações de ajustes de ponto dos funcionários.
                  </p>
                </div>
              </div>
              <div className="manager-header-actions">
                {permissions.canCreate && (
                  <Button onClick={handleCreate} className="manager-header-button" style={{display: 'none'}}>
                    <FiPlus size={18} />
                    Nova Solicitação
                  </Button>
                )}
              </div>
            </div>

            <Card className="manager-filters-card">
              <div className="manager-filters-header">
                <div className="manager-filters-title">
                  <FiFilter size={16} />
                  <span>Filtros</span>
                </div>
                {adjustments.length > 0 && (
                  <span className="manager-filters-count">
                    {filteredAdjustments.length} de {adjustments.length} solicitações
                  </span>
                )}
              </div>

              <div className="manager-filters-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div className="manager-filter-group">
                  <DatePeriodFilter
                    periodMode={periodMode}
                    setPeriodMode={setPeriodMode}
                    currentDate={currentDate}
                    setCurrentDate={setCurrentDate}
                    customRange={customPeriod}
                    setCustomRange={setCustomPeriod}
                  />
                </div>

                <div className="manager-filter-group">
                  <label className="manager-filter-label">Empresa</label>
                  <MultiSelect
                    id="company-filter"
                    options={[{ value: 'all', label: 'TODAS' }, ...companies.map(c => ({
                      value: String(c.id),
                      label: c.corporate_name || c.trade_name || c.name || `Empresa ${c.id}`
                    }))]}
                    value={companyFilterValues}
                    onChange={(newValues) => {
                      const allValue = 'all';
                      const currentSelected = companyFilterValues;
                      const isAllSelected = newValues.includes(allValue);
                      const wasAllSelected = currentSelected.includes(allValue);

                      let nextValues;

                      if (isAllSelected && !wasAllSelected) {
                        const allIds = companies.map(c => String(c.id));
                        nextValues = [allValue, ...allIds];
                      } else if (!isAllSelected && wasAllSelected) {
                        nextValues = [];
                      } else if (wasAllSelected && newValues.length < currentSelected.length) {
                        const remaining = newValues.filter(v => v !== allValue);
                        nextValues = remaining;
                      } else {
                        const specificIds = newValues.filter(v => v !== allValue);
                        const allIds = companies.map(c => String(c.id));
                        if (specificIds.length === allIds.length && allIds.length > 0) {
                          nextValues = [allValue, ...specificIds];
                        } else {
                          nextValues = specificIds;
                        }
                      }

                      setCompanyFilterValues(nextValues);

                      const idsWithoutAll = nextValues.filter(v => v !== allValue);
                      if (idsWithoutAll.length === 1) {
                        const id = idsWithoutAll[0];
                        setSelectedCompanyId(id);
                        loadAdjustments(id);
                      } else {
                        setSelectedCompanyId('all');
                        loadAdjustments('all');
                      }
                    }}
                    searchable
                  />
                </div>

                <div className="manager-filter-group">
                  <label className="manager-filter-label">Status</label>
                  <Select
                    placeholder="Filtrar por Status"
                    options={[
                      { value: 'all', label: 'TODOS' },
                      { value: 'pending', label: 'Pendente' },
                      { value: 'approved', label: 'Aprovado' },
                      { value: 'rejected', label: 'Rejeitado' },
                    ]}
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    searchable={false}
                  />
                </div>

                <div className="manager-filter-group">
                  <label className="manager-filter-label">Posto de Serviço</label>
                  <Select
                    placeholder="Todos os Postos"
                    options={[{ value: '', label: 'TODOS' }, ...serviceStations]}
                    value={selectedServiceStation}
                    onChange={(e) => setSelectedServiceStation(e.target.value)}
                    searchable
                  />
                </div>

                <div className="manager-filter-group">
                  <label className="manager-filter-label">Funcionário</label>
                  <Select
                    placeholder="Todos os Funcionários"
                    options={[{ value: '', label: 'TODOS' }, ...employees]}
                    value={selectedEmployee}
                    onChange={(e) => setSelectedEmployee(e.target.value)}
                    searchable
                  />
                </div>

                <div className="manager-filter-group">
                  <label className="manager-filter-label">Buscar</label>
                  <div className="manager-filter-search">
                    <Input
                      id="adjustment-search"
                      placeholder="Busque por nome ou motivo"
                      value={searchTerm}
                      onChange={event => setSearchTerm(event.target.value)}
                      icon={FiSearch}
                    />
                  </div>
                </div>
              </div>
            </Card>

            {error && <div className="error">{error}</div>}

            {!loading && !error && adjustments.length === 0 && (
              <div className="empty-state">
                <p>Nenhuma solicitação encontrada.</p>
              </div>
            )}

            {!loading && !error && adjustments.length > 0 && (
              <Table
                items={paginatedAdjustments}
                columns={columns}
                totalItems={totalItems}
                page={safePage}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={value => {
                  setPageSize(value);
                  setCurrentPage(1);
                }}
                getRowKey={item => item.id}
                emptyMessage="Nenhuma solicitação encontrada com os filtros atuais."
              />
            )}
          </>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={analyzingAdjustment ? 'Analisar Solicitação' : (selectedAdjustment ? 'Editar Solicitação' : 'Nova Solicitação')}
        closeOnOverlayClick={false}
        size={analyzingAdjustment ? 'xl' : 'md'}
      >
        {analyzingAdjustment ? (
          <AdjustmentAnalysisForm
            data={analyzingAdjustment}
            companyId={analyzingAdjustment.company_id || selectedCompanyId}
            onSuccess={handleFormSuccess}
            onCancel={() => setIsModalOpen(false)}
          />
        ) : (
          <AdjustmentForm
            initialData={selectedAdjustment}
            companyId={selectedAdjustment?.company_id || (selectedCompanyId === 'all' ? '' : selectedCompanyId)}
            onSuccess={handleFormSuccess}
            onCancel={() => setIsModalOpen(false)}
          />
        )}
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={handleCancelDelete}
        title="Confirmar exclusão"
        closeOnOverlayClick={!isDeleting}
      >
        <div className="delete-modal-content">
          <p>
            Tem certeza que deseja excluir a solicitação de{' '}
            {adjustmentToDelete && (
              <span className="delete-modal-company-name">
                {adjustmentToDelete.employee?.name}
              </span>
            )}
            ?
          </p>
          <p>
            Essa ação não poderá ser desfeita.
          </p>
        </div>
        <div className="delete-modal-actions">
          <Button
            type="button"
            className="cancel-button"
            onClick={handleCancelDelete}
            disabled={isDeleting}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Excluindo...' : 'Excluir'}
          </Button>
        </div>
      </Modal>
    </PageComponent>
  );
}
